import { Link } from 'react-router-dom';
import { useState } from 'react';
import { MarketingHeader } from '../../components/marketing/MarketingHeader';
import { MarketingFooter } from '../../components/marketing/MarketingFooter';
import { useAuth } from '../auth/AuthContext';
import { useSubscription } from '../subscription/SubscriptionContext';
import { redirectToCheckout } from '../../services/stripeService';
import { errorService } from '../../services/errorService';
import { notificationService } from '../../services/notificationService';
import { SEOHead } from '../../components/SEO/SEOHead';
import { StructuredData } from '../../components/SEO/StructuredData';
import { createBreadcrumbListSchema } from '../../components/SEO/StructuredData';

export const PricingPage = () => {
  const { user } = useAuth();
  const { subscriptionTier, hasStripeCustomer, manageSubscription } = useSubscription();
  const ctaLink = user ? '/workspace' : '/signup';
  const [isUpgrading, setIsUpgrading] = useState(false);
  const [isManaging, setIsManaging] = useState(false);
  const [isAnnual, setIsAnnual] = useState(false);
  
  const showManageButton = user && hasStripeCustomer;

  const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://simcarlo.com';

  const handleUpgradeToPro = async (annual: boolean) => {
    if (!user) {
      // Redirect to signup if not logged in
      window.location.href = '/signup';
      return;
    }

    setIsUpgrading(true);
    try {
      const priceId = annual 
        ? import.meta.env.VITE_STRIPE_PRO_ANNUAL_PRICE_ID
        : import.meta.env.VITE_STRIPE_PRO_MONTHLY_PRICE_ID;
      
      if (!priceId) {
        throw new Error('Stripe price ID not configured');
      }
      
      await redirectToCheckout(priceId);
    } catch (error) {
      errorService.logError('Failed to upgrade', error, { action: 'upgrade' });
      notificationService.showError('Failed to start upgrade process. Please try again.');
      setIsUpgrading(false);
    }
  };

  const handleManageSubscription = async () => {
    setIsManaging(true);
    try {
      await manageSubscription('/pricing');
    } catch (error) {
      errorService.logError('Failed to manage subscription', error, { action: 'manage_subscription' });
      notificationService.showError('Failed to open subscription management. Please try again.');
      setIsManaging(false);
    }
  };

  return (
    <div className="marketing-page">
      <SEOHead
        metadata={{
          title: 'Pricing - Simple, Transparent Plans',
          description: 'Start free, upgrade when ready. Free forever plan available. Pro plans from €15/month with 14-day free trial. No credit card required.',
          keywords: ['pricing', 'plans', 'subscription', 'free trial', 'Monte Carlo simulation pricing'],
          ogImage: `${baseUrl}/images/Landing Main.png`,
          canonicalUrl: `${baseUrl}/pricing`,
        }}
      />
      <StructuredData
        schemas={[
          createBreadcrumbListSchema([
            { name: 'Home', url: baseUrl },
            { name: 'Pricing', url: `${baseUrl}/pricing` },
          ]),
        ]}
      />
      <MarketingHeader />

      {/* Hero Section */}
      <section className="marketing-hero marketing-pricing-hero">
        <div className="marketing-container">
          <div className="marketing-pricing-header">
            <h1 className="marketing-section-title">Simple, Transparent Pricing</h1>
            <p className="marketing-section-description">Start free, upgrade when you're ready. No surprises.</p>
            {showManageButton && (
              <button 
                onClick={handleManageSubscription}
                className="marketing-btn marketing-btn-primary"
                disabled={isManaging}
                style={{ marginTop: '1rem' }}
              >
                {isManaging ? 'Opening...' : 'Manage Your Subscription'}
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="marketing-pricing-section">
        <div className="marketing-container">
          {/* Billing Toggle */}
          <div className="marketing-pricing-toggle">
            <span className={`marketing-pricing-toggle-label ${!isAnnual ? 'active' : ''}`}>
              Monthly
            </span>
            <label className="marketing-pricing-switch">
              <input
                type="checkbox"
                checked={isAnnual}
                onChange={(e) => setIsAnnual(e.target.checked)}
              />
              <span className="marketing-pricing-slider"></span>
            </label>
            <span className={`marketing-pricing-toggle-label ${isAnnual ? 'active' : ''}`}>
              Annual
            </span>
          </div>
          <div className="marketing-pricing-grid">
            {/* Free Tier */}
            <div className="marketing-pricing-card">
              <h3 className="marketing-pricing-tier">Free</h3>
              <div className="marketing-pricing-price">
                <span className="marketing-price-amount">€0</span>
                <span className="marketing-price-period">/forever</span>
              </div>
              <p className="marketing-pricing-description">Perfect for trying out the platform</p>
              <ul className="marketing-pricing-features">
                <li>✓ 1 simulation at a time</li>
                <li>✓ 20 AI assistant requests (lifetime)</li>
                <li>✓ Visual modeling</li>
                <li>✓ Export results to JSON</li>
                <li>✓ Version history</li>
              </ul>
              <Link to={ctaLink} className="marketing-btn marketing-btn-secondary marketing-btn-large">
                Get Started Free
              </Link>
            </div>

            {/* Pro Tier */}
            <div className="marketing-pricing-card marketing-pricing-featured">
              <div className="marketing-pricing-badge">Most Popular</div>
              <div className="marketing-pricing-tier-row">
                <h3 className="marketing-pricing-tier">Pro</h3>
                {isAnnual && (
                  <div className="marketing-pricing-savings-badge-inline">Save 13%</div>
                )}
              </div>
              <div className="marketing-pricing-price">
                <span className="marketing-price-amount">
                  {isAnnual ? '€13' : '€15'}
                </span>
                <span className="marketing-price-period">/month</span>
              </div>
              {isAnnual && (
                <>
                  <p className="marketing-pricing-equivalent">
                    billed annually
                  </p>
                  <p className="marketing-pricing-total">
                    €156/year total
                  </p>
                </>
              )}
              <p className="marketing-pricing-description">For professionals who need unlimited power</p>
              <ul className="marketing-pricing-features">
                <li>✓ Everything in Free</li>
                <li>✓ Unlimited simulations</li>
                <li>✓ Unlimited AI assistant requests</li>
                <li>✓ Export to Excel</li>
                <li>✓ Priority support</li>
                <li>✓ 14-day free trial</li>
              </ul>
              <button 
                onClick={() => handleUpgradeToPro(isAnnual)} 
                className="marketing-btn marketing-btn-primary marketing-btn-large"
                disabled={isUpgrading}
              >
                {isUpgrading ? 'Redirecting...' : 'Start 14-Day Free Trial'}
              </button>
            </div>

            {/* Team Tier */}
            <div className="marketing-pricing-card marketing-pricing-disabled">
              <div className="marketing-pricing-badge marketing-pricing-badge-coming-soon">Coming Soon</div>
              <div className="marketing-pricing-tier-row">
                <h3 className="marketing-pricing-tier">Team</h3>
                {isAnnual && (
                  <div className="marketing-pricing-savings-badge-inline">Save 14%</div>
                )}
              </div>
              <div className="marketing-pricing-price">
                <span className="marketing-price-amount">
                  {isAnnual ? '€30' : '€35'}
                </span>
                <span className="marketing-price-period">/month</span>
              </div>
              {isAnnual && (
                <>
                  <p className="marketing-pricing-equivalent">
                    billed annually
                  </p>
                  <p className="marketing-pricing-total">
                    €360/year total
                  </p>
                </>
              )}
              <p className="marketing-pricing-description">Planned for teams that need shared model governance</p>
              <ul className="marketing-pricing-features">
                <li>✓ Everything in Pro</li>
                <li>✓ Team member management</li>
                <li>✓ Shared model workspace</li>
                <li>✓ Admin controls</li>
                <li>✓ Review workflows</li>
                <li>✓ API access planned</li>
                <li>✓ Team support planned</li>
              </ul>
              <button className="marketing-btn marketing-btn-secondary marketing-btn-large" disabled>
                Coming Soon
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="marketing-pricing-faq">
        <div className="marketing-container">
          <h2 className="marketing-section-title">Frequently Asked Questions</h2>
          <div className="marketing-faq-grid">
            <div className="marketing-faq-item">
              <h3>Can I upgrade or downgrade anytime?</h3>
              <p>Yes! You can change your plan at any time. Upgrades take effect immediately, and downgrades apply at the end of your billing period.</p>
            </div>
            <div className="marketing-faq-item">
              <h3>What happens to my data if I downgrade?</h3>
              <p>Your simulations and data are safe. If you have more than 1 simulation, your account becomes read-only until you upgrade or delete simulations. AI Assistant requests are limited to 20 lifetime on the free plan.</p>
            </div>
            <div className="marketing-faq-item">
              <h3>Do you offer annual billing?</h3>
              <p>Yes. Annual billing saves about 13% compared with monthly billing. You can switch between monthly and annual billing using the toggle above.</p>
            </div>
            <div className="marketing-faq-item">
              <h3>Is there a free trial for paid plans?</h3>
              <p>Yes. The Pro plan includes a 14-day free trial. Team plans are coming soon.</p>
            </div>
          </div>
          <div className="marketing-pricing-cta">
            <p>Have more questions? <Link to="/faq">Visit our FAQ page</Link> or <a href="#contact">contact us</a>.</p>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};
